import React, { useState, useMemo, useEffect, useCallback } from 'react';
import toast from 'react-hot-toast';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    useTheme,
    useMediaQuery,
    Box,
    Typography,
    Button,
    Chip,
    TextField,
    Autocomplete,
    Divider,
    Checkbox,
    Avatar,
    CircularProgress,
} from '@mui/material';
import {
    CheckBox, 
    CheckBoxOutlineBlank,
    SelectAll, 
    DeselectOutlined, 
    FilterAlt,
} from '@mui/icons-material';
import { useDateRangeContext } from 'contexts/DateRangeContext';
import api from 'state/axios';
import DateRangeField from 'components/DateRangeField';
import FlexBetween from 'components/FlexBetween';


const icon        = <CheckBoxOutlineBlank fontSize="small" />;
const checkedIcon = <CheckBox fontSize="small" />;

const MultiSelect = ({ label, options, value, onChange }) => {
    const isXs = useMediaQuery('(max-width:599px)');
    return (
        <Box mb={1}>
            <Typography variant="caption" color="text.secondary" sx={{ display: { xs: 'none', sm: 'block' }, mb: 0.5 }}>
                {label}
            </Typography>
            <Autocomplete
                multiple size="small"
                options={options}
                value={value}
                onChange={(_, v) => onChange(v)}
                disableCloseOnSelect
                renderOption={(props, option, { selected }) => (
                    <li {...props} key={option}>
                        <Checkbox icon={icon} checkedIcon={checkedIcon} checked={selected} size="small" sx={{ mr: 1, p: 0.5 }} />
                        <Typography variant="body2">{option}</Typography>
                    </li>
                )}
                slotProps={{ chip: { size: 'small' } }}
                renderInput={(params) => <TextField {...params} label={isXs ? label : undefined} />}
            />
        </Box>
    );
};

const PublishScheduleDialog = ({
    open,
    onClose,
    allEmployees = [],
    onPublished,
    canPublish = false,
}) => {
    const theme = useTheme();
    const isXs  = useMediaQuery('(max-width:599px)');
    const { startDate: ctxStart, endDate: ctxEnd } = useDateRangeContext();

    const [localStart, setLocalStart]     = useState(null);
    const [localEnd,   setLocalEnd]       = useState(null);

    const [selEmployees, setSelEmployees] = useState([]);
    const [selRanks, setSelRanks]         = useState([]);
    const [selBases, setSelBases]         = useState([]);
    const [selDepts, setSelDepts]         = useState([]);
    const [selDivs, setSelDivs]           = useState([]);

    const [displayed, setDisplayed]       = useState([]);
    const [checked, setChecked]           = useState([]);
    const [publishing, setPublishing]     = useState(false);

    /* init: show all employees + sync dates from context when dialog opens */
    useEffect(() => {
        if (open) {
            setLocalStart(ctxStart ? new Date(ctxStart) : null);
            setLocalEnd(ctxEnd   ? new Date(ctxEnd)   : null);
            setDisplayed(allEmployees);
            setChecked(allEmployees.map(e => e._id));
            setSelEmployees([]);
            setSelRanks([]);
            setSelBases([]);
            setSelDepts([]);
            setSelDivs([]);
        }
    }, [open, allEmployees]); // eslint-disable-line react-hooks/exhaustive-deps

    const options = useMemo(() => ({
        ranks: (() => {
            const priorityMap = {};
            allEmployees.forEach(e => {
                if (e.rank && priorityMap[e.rank] === undefined)
                    priorityMap[e.rank] = e.rankPriority ?? Infinity;
            });
            return Object.keys(priorityMap).sort((a, b) => priorityMap[a] - priorityMap[b]);
        })(),
        bases: [...new Set(allEmployees.map(e => e.homeBase).filter(Boolean))].sort(),
        depts: [...new Set(allEmployees.map(e => e.department).filter(Boolean))].sort(),
        divs:  [...new Set(allEmployees.map(e => e.division).filter(Boolean))].sort(),
    }), [allEmployees]);

    /* ── Filter ── */
    const handleFilter = useCallback(() => {
        const pickedIds = new Set(selEmployees.map(e => e._id));
        const result = allEmployees.filter(e => {
            if (pickedIds.size  && !pickedIds.has(e._id))              return false;
            if (selRanks.length && !selRanks.includes(e.rank))         return false;
            if (selBases.length && !selBases.includes(e.homeBase))     return false;
            if (selDepts.length && !selDepts.includes(e.department))   return false;
            if (selDivs.length  && !selDivs.includes(e.division))      return false;
            return true;
        });
        setDisplayed(result);
        setChecked(result.map(e => e._id));
    }, [allEmployees, selEmployees, selRanks, selBases, selDepts, selDivs]);

    /* ── Check / Uncheck ── */
    const toggleCheck = (id) =>
        setChecked(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
    const selectAll   = () => setChecked(displayed.map(e => e._id));
    const deselectAll = () => setChecked([]);

    const allChecked  = displayed.length > 0 && checked.length === displayed.length;
    const someChecked = checked.length > 0 && !allChecked;

    /* ── Publish ── */
    const handlePublish = async () => {
        if (!localStart || !localEnd) { toast.error('Please select a date period.'); return; }
        if (!checked.length)          { toast.error('No employees selected.');        return; }
        setPublishing(true);
        try {
            const res = await api.post('/assignments/publish-bulk', {
                employeeIds: checked,
                startDate: localStart,
                endDate:   localEnd,
            });
            toast.success(res.data.message || 'Published.');
            onPublished?.();
            onClose();
        } catch (err) {
            toast.error(err.response?.data?.message || 'Publish failed.');
        } finally {
            setPublishing(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle sx={{ pb: 1 }}>
                <Box display="flex" alignItems="center" gap={1}>
                    {/* <Publish color="primary" /> */}
                    <Box>
                        <Typography variant="subtitle1" fontWeight="bold" lineHeight={1.2}>
                            Publish Schedule
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            Filter employees, then select who to publish
                        </Typography>
                    </Box>
                </Box>
            </DialogTitle>

            <Divider />

            <DialogContent sx={{ p: 0 }}>
                <Box
                    display="flex"
                    sx={{
                        flexDirection: { xs: 'column', sm: 'row' },
                        height: { xs: 'auto', sm: 480 },
                    }}
                >

                    {/* ── LEFT: filter criteria ── */}
                    <Box
                        sx={{
                            width: { xs: '100%', sm: 250 },
                            flexShrink: 0,
                            p: 2,
                            overflowY: 'auto',
                            borderRight: { xs: 'none', sm: `1px solid ${theme.palette.divider}` },
                            borderBottom: { xs: `1px solid ${theme.palette.divider}`, sm: 'none' },
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 0,
                        }}
                    >
                        {/* Date Period */}
                        <Box mb={1}>
                            <Typography variant="caption" color="text.secondary" sx={{ display: { xs: 'none', sm: 'block' }, mb: 0.5 }}>
                                Date Period
                            </Typography>
                            <DateRangeField
                                fullWidth
                                startDate={localStart}
                                endDate={localEnd}
                                onChange={([s, e]) => { setLocalStart(s); setLocalEnd(e); }}
                            />
                        </Box>

                        {/* Employee autocomplete */}
                        <Box mb={1}>
                            <Typography variant="caption" color="text.secondary" sx={{ display: { xs: 'none', sm: 'block' }, mb: 0.5 }}>
                                Employee
                            </Typography>
                            <Autocomplete
                                multiple size="small"
                                options={allEmployees}
                                value={selEmployees}
                                onChange={(_, v) => setSelEmployees(v)}
                                getOptionLabel={e => e.employeeCode}
                                filterOptions={(opts, { inputValue }) => {
                                    const q = inputValue.toLowerCase();
                                    return opts.filter(e =>
                                        e.employeeCode?.toLowerCase().includes(q) ||
                                        e.employeeName?.toLowerCase().includes(q)
                                    );
                                }}
                                isOptionEqualToValue={(a, b) => a._id === b._id}
                                disableCloseOnSelect
                                slotProps={{ chip: { size: 'small' } }}
                                renderOption={(props, option, { selected }) => (
                                    <li {...props} key={option._id}>
                                        <Checkbox icon={icon} checkedIcon={checkedIcon} checked={selected} size="small" sx={{ mr: 1, p: 0.5 }} />
                                        <Box>
                                            <Typography variant="body2" lineHeight={1.2}>{option.employeeName}</Typography>
                                            <Typography variant="caption" color="text.secondary">{option.employeeCode}</Typography>
                                        </Box>
                                    </li>
                                )}
                                renderInput={(params) => <TextField {...params} label={isXs ? 'Employee' : undefined} />}
                            />
                        </Box>

                        <MultiSelect label="Rank"       options={options.ranks} value={selRanks} onChange={setSelRanks} />
                        <MultiSelect label="Base"       options={options.bases} value={selBases} onChange={setSelBases} />
                        <MultiSelect label="Department" options={options.depts} value={selDepts} onChange={setSelDepts} />
                        <MultiSelect label="Division"   options={options.divs}  value={selDivs}  onChange={setSelDivs}  />

                        <Box pt={1}>
                            <Button
                                fullWidth
                                variant="outlined"
                                startIcon={<FilterAlt />}
                                onClick={handleFilter}
                            >
                                Filter
                            </Button>
                        </Box>
                    </Box>

                    {/* ── RIGHT: employee list ── */}
                    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0, minHeight: { xs: 320, sm: 'unset' } }}>

                        {/* Select all bar */}
                        <FlexBetween
                            px={1.5} py={0.75}
                            sx={{ borderBottom: `1px solid ${theme.palette.divider}`, flexShrink: 0 }}
                        >
                            <Box display="flex" alignItems="center" gap={0.5}>
                                <Checkbox
                                    size="small"
                                    checked={allChecked}
                                    indeterminate={someChecked}
                                    onChange={allChecked ? deselectAll : selectAll}
                                />
                                <Typography variant="caption" color="text.secondary">
                                    {checked.length} / {displayed.length} selected
                                </Typography>
                            </Box>
                            <Box display="flex" gap={0.5}>
                                <Button size="small" startIcon={<SelectAll />} onClick={selectAll}>All</Button>
                                <Button size="small" startIcon={<DeselectOutlined />} onClick={deselectAll}>None</Button>
                            </Box>
                        </FlexBetween>

                        {/* Scrollable list */}
                        <Box sx={{ flex: 1, overflowY: 'auto' }}>
                            {displayed.length === 0 ? (
                                <FlexBetween height="100%">
                                    <Typography variant="body2" color="text.secondary">
                                        No employees match the criteria.
                                    </Typography>
                                </FlexBetween>
                            ) : (
                                displayed.map((emp, idx) => {
                                    const isChecked = checked.includes(emp._id);
                                    return (
                                        <Box
                                            key={emp._id}
                                            onClick={() => toggleCheck(emp._id)}
                                            sx={{
                                                display: 'flex', alignItems: 'center', gap: 1.5,
                                                px: 1.5, py: 0.75, cursor: 'pointer',
                                                bgcolor: isChecked
                                                    ? `${theme.palette.primary.main}14`
                                                    : idx % 2 === 0 ? 'transparent' : 'action.hover',
                                                borderBottom: `1px solid ${theme.palette.divider}`,
                                                '&:last-child': { borderBottom: 'none' },
                                                '&:hover': { bgcolor: `${theme.palette.primary.main}22` },
                                            }}
                                        >
                                            <Checkbox
                                                size="small"
                                                checked={isChecked}
                                                onChange={() => toggleCheck(emp._id)}
                                                onClick={e => e.stopPropagation()}
                                            />
                                            <Avatar sx={{ width: 28, height: 28, fontSize: 12, bgcolor: 'primary.main' }}>
                                                {emp.employeeName?.charAt(0)?.toUpperCase() || '?'}
                                            </Avatar>
                                            <Box flex={1} minWidth={0}>
                                                <Typography variant="body2" fontWeight="500" noWrap>
                                                    {emp.employeeName}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    {emp.employeeCode}
                                                </Typography>
                                            </Box>
                                            <Chip label={emp.rank || '—'} size="small" variant="outlined" sx={{ fontSize: '0.65rem' }} />
                                            <Typography variant="caption" color="text.secondary" sx={{ minWidth: 36, textAlign: 'right' }}>
                                                {emp.homeBase}
                                            </Typography>
                                        </Box>
                                    );
                                })
                            )}
                        </Box>
                    </Box>

                </Box>
            </DialogContent>

            <Divider />

            <DialogActions sx={{ px: 2, py: 1.5, justifyContent: 'space-between' }}>
                <Typography variant="caption" color="text.secondary">
                    {checked.length} employee{checked.length !== 1 ? 's' : ''} will be published
                </Typography>
                <Box display="flex" gap={1}>
                    <Button 
                        variant="outlined"
                        color="secondary"
                        onClick={onClose} 
                        disabled={publishing}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="outlined"
                        color="success"
                        startIcon={publishing ? <CircularProgress size={14} color="inherit" /> : null}
                        disabled={publishing || !checked.length || !canPublish}
                        onClick={handlePublish}
                    >
                        {publishing ? 'Publishing…' : 'Publish'}
                    </Button>
                </Box>
            </DialogActions>
        </Dialog>
    );
};

export default PublishScheduleDialog;
